import {
  Box,
  Tooltip,
  Button,
  MaterialSymbol,
  formattedCurrency,
  capitalizeFirstLetter,
  createTableProps,
  Truncate,
  type TableProps,
  type TransactionsLists,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import { Table, CopyButton, TransactionTypeLabel } from '#shared/components/UI';
import TransactionStatus from './TransactionStatus/TransactionStatus';
import { normalizeStatus } from './TransactionStatus/normalizeStatus';
import useTransactionHistory from './useTransactionHistory';
import styles from './Transactions.module.scss';

type UseTableConfigArgs = {
  filteredList: TransactionsLists[];
  exportTransactions: TransactionsLists[];
  includeSubAccounts: boolean;
  onFlagClick: (transactionID: string, flag: string) => void;
  onRemoveFlagClick: (transactionID: string) => void;
};

// The Transaction History table column config, extracted from the page. `tableProps` drives the on-screen
// table (vopay Table2 via createTableProps); `exportTableProps` drives the hidden legacy Table1 the
// client-side export scrapes (plain text, no wordmarks). Kept as one hook so the page stays about
// data-flow, not column markup. The row's detail link is host-owned — the transaction-detail page stays
// in the host, reached through hostPaths.
export default function useTableConfig({
  filteredList,
  exportTransactions,
  includeSubAccounts,
  onFlagClick,
  onRemoveFlagClick,
}: UseTableConfigArgs) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.TransactionHistory' });
  // The hidden export table needs the type as plain, serializable text (no InteracWordmark markup),
  // so it reads the shared TransactionTypes label map directly — the same source the on-screen
  // TransactionTypeLabel falls back to — instead of the on-screen component.
  const { t: tTypes } = useTranslation('translations', { keyPrefix: 'Shared.TransactionTypes' });
  const { hostPaths, userPermissions, showAccountName, hiddenColumns = [] } = useTransactionHistory();
  const canRemoveFlags = userPermissions.canRemoveFlags ?? true;

  const tableProps = createTableProps({
    id: 'transactionHistoryTable',
    zebra: true,
    columns: [
      {
        id: 'transactionDate',
        header: t('Table.transactionDate'),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => (
          <Box gap="sm" ai="center">
            {transaction.data.TransactionFlag !== '' && (
              <Tooltip
                information={transaction.data.TransactionFlag}
                hasButton={false}
                customElement={
                  canRemoveFlags ? (
                    <Button
                      id={`flag-button-${transaction.data.TransactionID}`}
                      color="none"
                      background="transparent"
                      label={<MaterialSymbol fillAmount={1} icon="flag" size="24px" color="rgba(189, 58, 45, 1)" />}
                      noPadding
                      noShadow
                      onClick={() => onFlagClick(transaction.data.TransactionID, transaction.data.TransactionFlag)}
                    />
                  ) : (
                    <MaterialSymbol fillAmount={1} icon="flag" size="24px" color="rgba(189, 58, 45, 1)" />
                  )
                }
                position="right"
              />
            )}

            <span>{transaction.data.TransactionDateTime}</span>
          </Box>
        ),
      },
      {
        id: 'transId',
        header: t('Table.id'),
        align: 'left',
        style: {
          minWidth: '100px',
        },
        template: ({ data }) => (
          <Box gap="sm" ai="center">
            {hostPaths.transactionDetail ? (
              <Table.Link to={hostPaths.transactionDetail(data)}>{data.TransactionID}</Table.Link>
            ) : (
              <span>{data.TransactionID}</span>
            )}

            <CopyButton content={data.TransactionID} color="rgba(45, 45, 45, 0.6)" />
          </Box>
        ),
      },
      {
        id: 'accountName',
        header: t('Table.accountName'),
        align: 'left',
        field: 'AccountName',
        visible: showAccountName || includeSubAccounts,
      },
      {
        id: 'type',
        header: t('Table.type'),
        align: 'left',
        template: (transaction) => <TransactionTypeLabel transactionType={transaction.data.TransactionType} />,
        style: {
          // The renamed rail labels are long ('Interac e-Transfer® Request Money'), so this
          // needs more room than the old raw keys did.
          minWidth: '160px',
        },
      },
      {
        id: 'paymentRailID',
        header: t('Table.paymentRailID', { defaultValue: 'Payment Rail ID' }),
        align: 'left',
        field: 'PaymentRailID',
        visible: !hiddenColumns.includes('paymentRailID'),
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'name',
        header: t('Table.name'),
        align: 'left',
        field: 'FullName',
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'referenceNumber',
        header: t('Table.referenceNumber'),
        align: 'left',
        field: 'ClientReferenceNumber',
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'credit',
        header: t('Table.credit'),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => (
          <>
            {transaction.data.CreditAmount !== '0.00' && (
              <Box flx="col" gapSize="6px">
                <span>{`${transaction.data.Currency} ${formattedCurrency(transaction.data.CreditAmount)}`}</span>

                {transaction.data.HoldAmount !== '0.00' && (
                  <span className={styles.pendingAmount}>
                    {t('Table.pending')}{' '}
                    {`${transaction.data.Currency} ${formattedCurrency(transaction.data.HoldAmount)}`}
                  </span>
                )}
              </Box>
            )}
          </>
        ),
      },
      {
        id: 'debit',
        header: t('Table.debit'),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => (
          <>
            {transaction.data.DebitAmount !== '0.00' && (
              <Box flx="col" gapSize="6px">
                <span>{`${transaction.data.Currency} ${formattedCurrency(transaction.data.DebitAmount)}`}</span>

                {transaction.data.HoldAmount !== '0.00' && (
                  <span className={styles.pendingAmount}>
                    {t('Table.pending')}{' '}
                    {`${transaction.data.Currency}  ${formattedCurrency(transaction.data.HoldAmount)}`}
                  </span>
                )}
              </Box>
            )}
          </>
        ),
      },
      {
        id: 'status',
        header: t('Table.status'),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => (
          <Box gapSize="10px">
            <TransactionStatus
              status={normalizeStatus(transaction.data.TransactionStatus)}
              label={
                capitalizeFirstLetter(
                  t(`Statuses.${transaction.data.TransactionStatus}`, {
                    defaultValue: transaction.data.TransactionStatus,
                  }),
                ) || ''
              }
            />

            {transaction.data.TransactionFailureReason && (
              <Tooltip
                information={transaction.data.TransactionFailureReason}
                icon="info"
                variant="outlined"
                iconWidth="14px"
                buttonWidth="22px"
                hasButton={true}
                position="bottomLeft"
              />
            )}
          </Box>
        ),
      },
      {
        id: 'settlementDate',
        header: t('Table.settlementDate'),
        align: 'left',
        field: 'SettlementDate',
        visible: !hiddenColumns.includes('settlementDate'),
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'notes',
        header: t('Table.notes'),
        align: 'left',
        style: {
          minWidth: '220px',
        },
        template: (transaction) => {
          const isTruncate = transaction.data.Notes.length > 25;

          return (
            <Tooltip
              information={transaction.data.Notes}
              isDisabled={!isTruncate}
              hasButton={false}
              customElement={
                <Box gap="sm" ai="center" jc="sb" className="notes">
                  <Truncate text={transaction.data.Notes} maxLength={25} truncateEnding="..." />

                  {transaction.data.Notes && (
                    <CopyButton content={transaction.data.Notes} color="rgba(45, 45, 45, 0.6)" />
                  )}
                </Box>
              }
              position="bottomLeft"
            />
          );
        },
      },
    ],
    rows: filteredList.map((transaction): { id: string; data: TransactionsLists } => ({
      id: transaction.TransactionID,
      data: transaction,
    })),
  });

  const exportTableProps: TableProps = {
    id: 'export-transaction-history-table',
    name: 'transactions',
    columns: {
      id: { header: t('Table.id'), align: 'left' },
      name: { header: t('Table.name'), align: 'left' },
      transactionDate: { header: t('Table.transactionDate'), align: 'left' },
      type: { header: t('Table.type') || '', align: 'left' },
      referenceNumber: { header: t('Table.referenceNumber'), align: 'left' },
      amount: { header: t('Table.amount') },
      credit: { header: t('Table.credit') },
      debit: { header: t('Table.debit') },
      pending: { header: t('Table.pending') },
      flag: { header: t('Table.flag') },
      status: { header: t('Table.status') },
      failureReason: { header: t('Table.failureReason') },
      notes: { header: t('Table.notes') },
    },
    rows: exportTransactions.map((transaction, index) => ({
      id: transaction.TransactionID,
      name: transaction.FullName,
      transactionDate: transaction.TransactionDateTime,
      type: tTypes(transaction.TransactionType, {
        defaultValue: transaction.TransactionType,
      }),
      referenceNumber: transaction.ClientReferenceNumber,
      amount: `$${
        transaction.CreditAmount === '0.00'
          ? formattedCurrency(transaction.DebitAmount)
          : formattedCurrency(transaction.CreditAmount)
      }`,
      credit: transaction.CreditAmount !== '0.00' ? `$${formattedCurrency(transaction.CreditAmount)}` : '',
      debit: transaction.DebitAmount !== '0.00' ? `$${formattedCurrency(transaction.DebitAmount)}` : '',
      pending: `$${formattedCurrency(transaction.HoldAmount)}`,
      flag: (
        <div className={styles.flagSection}>
          <span>{transaction.TransactionFlag} </span>

          {transaction.TransactionFlag !== '' ? (
            <Button
              id={`remove-flag-${index}`}
              label={t('Table.removeFlag')}
              onClick={() => onRemoveFlagClick(transaction.TransactionID)}
            />
          ) : (
            <></>
          )}
        </div>
      ),
      status:
        capitalizeFirstLetter(
          t(`Statuses.${transaction.TransactionStatus}`, {
            defaultValue: transaction.TransactionStatus,
          }),
        ) || '',
      failureReason: transaction.TransactionFailureReason,
      notes: transaction.Notes,
    })),
  };

  return { tableProps, exportTableProps };
}
